import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import { Menu, X, Phone, Mail, Globe } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { useTranslation } from "../hooks/useTranslation";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const { language, toggleLanguage } = useLanguage();
  const { t } = useTranslation();

  const navigation = [
    { name: t('nav.home'), href: "/" },
    { name: t('nav.about'), href: "/about" },
    { name: t('nav.contact'), href: "/contact" }
  ];

  const isActivePath = (path) => location.pathname === path;

  return (
    <header className="shadow-lg sticky top-0 z-50" style={{ backgroundColor: "rgba(255, 255, 255, 0.5)", backdropFilter: "blur(10px)" }}>
      {/* Top bar */}
      <div style={{ backgroundColor: "#283747" }} className="text-white py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center text-sm">
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2">
                <Phone className="w-4 h-4" />
                <span>{t('header.phone')}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Mail className="w-4 h-4" />
                <span>{t('header.email')}</span>
              </div>
            </div>
            <div className="hidden md:block">
              <span>{t('header.hours')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo with actual brand image */}
          <Link to="/" className="flex items-center space-x-3">
            <div className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_aymorenov-catalog/artifacts/bkaifu5k_logo.png" 
                alt="AYMORENOV Logo" 
                className="h-8 md:h-16 lg:h-20 w-auto object-contain"
                style={{
                  height: "30px", // Mobile: 30px
                }}
                onLoad={(e) => {
                  // Apply responsive sizing
                  const updateLogoSize = () => {
                    if (window.innerWidth >= 768) {
                      e.target.style.height = "65px"; // Tablet ET Desktop: 65px
                    } else {
                      e.target.style.height = "30px"; // Mobile: 30px
                    }
                  };
                  updateLogoSize();
                  window.addEventListener('resize', updateLogoSize);
                }}
                onError={(e) => {
                  // Fallback logo if image fails to load
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="hidden flex items-center space-x-3">
                <div 
                  className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold text-xl"
                  style={{ backgroundColor: "#DC2626" }}
                >
                  A
                </div>
                <div>
                  <h1 className="text-xl font-bold" style={{ color: "#283747" }}>AYMORENOV</h1>
                  <p className="text-sm" style={{ color: "#DC2626" }}>CONSTRUCTION INC</p>
                </div>
              </div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                  isActivePath(item.href)
                    ? "text-white"
                    : "hover:text-white"
                }`}
                style={{
                  backgroundColor: isActivePath(item.href) ? "#DC2626" : "transparent",
                  color: isActivePath(item.href) ? "white" : "#283747"
                }}
                onMouseEnter={(e) => {
                  if (!isActivePath(item.href)) {
                    e.target.style.backgroundColor = "#D4A86A";
                    e.target.style.color = "white";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActivePath(item.href)) {
                    e.target.style.backgroundColor = "transparent";
                    e.target.style.color = "#283747";
                  }
                }}
              >
                {item.name}
              </Link>
            ))}
            
            {/* Language Switch Button */}
            <Button
              onClick={toggleLanguage}
              variant="ghost"
              size="sm"
              className="flex items-center space-x-2 px-3 py-2 rounded-md transition-colors duration-200 hover:bg-gray-100"
              style={{ color: "#283747" }}
            >
              <Globe className="w-4 h-4" />
              <span className="text-sm font-medium">{language.toUpperCase()}</span>
            </Button>
            
            <Link to="/quote">
              <Button 
                className="text-white font-medium px-6 py-2 rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg"
                style={{ backgroundColor: "#DC2626" }}
                onMouseEnter={(e) => e.target.style.backgroundColor = "#B91C1C"}
                onMouseLeave={(e) => e.target.style.backgroundColor = "#DC2626"}
              >
                {t('header.quoteButton')}
              </Button>
            </Link>
          </nav>

          {/* Mobile menu button and language switch */}
          <div className="md:hidden flex items-center space-x-2">
            {/* Language Switch Button for Mobile */}
            <Button
              onClick={toggleLanguage}
              variant="ghost"
              size="sm"
              className="flex items-center space-x-1 px-2 py-1 rounded-md transition-colors duration-200"
              style={{ color: "#283747" }}
            >
              <Globe className="w-4 h-4" />
              <span className="text-sm font-medium">{language.toUpperCase()}</span>
            </Button>
            
            {/* Menu Button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              style={{ color: "#283747" }}
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="md:hidden border-t border-slate-200" style={{ backgroundColor: "rgba(255, 255, 255, 0.5)", backdropFilter: "blur(10px)" }}>
          <div className="px-2 pt-2 pb-3 space-y-1">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`block px-3 py-2 rounded-md text-base font-medium transition-colors duration-200 ${
                  isActivePath(item.href)
                    ? "text-white"
                    : "hover:text-white"
                }`}
                style={{
                  backgroundColor: isActivePath(item.href) ? "#DC2626" : "transparent",
                  color: isActivePath(item.href) ? "white" : "#283747"
                }}
                onClick={() => setIsMenuOpen(false)}
              >
                {item.name}
              </Link>
            ))}
            <div className="px-3 pt-2">
              <Link to="/quote" onClick={() => setIsMenuOpen(false)}>
                <Button 
                  className="w-full text-white font-medium py-2 rounded-lg transition-all duration-300"
                  style={{ backgroundColor: "#DC2626" }}
                >
                  {t('header.quoteButton')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;